import { Link, createFileRoute } from "@tanstack/react-router";
import * as z from "zod/v4";
import { useForm } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { DialogDescription } from "@radix-ui/react-dialog";
import ky, { HTTPError } from "ky";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { api, getBackendUrl, logout, saveTokens } from "@/lib/http";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/passwordInput";
import { generateDefaultValue } from "@/lib/utils/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { parseBackendError } from "@/lib/utils/backend";

export const Route = createFileRoute("/auth/signin")({
  component: RouteComponent,
  staticData: {
    title: "Sign In",
  },
});

const LoginFormSchema = z.object({
  email: z
    .string("Email or username is required")
    .min(1, "Email or username is required"),
  password: z.string("Password is required").min(1, "Password is required"),
});
type LoginFormSchema = z.infer<typeof LoginFormSchema>;

type SigninResponse = { access_token: string } & (
  | {
      refresh_token: null;
      must_change_password: true;
    }
  | {
      refresh_token: string;
      must_change_password: false;
    }
);
function RouteComponent() {
  const navigate = Route.useNavigate();

  const [updateDialogPassword, setUpdateDialogPassword] = useState<
    string | null
  >(null);

  const form = useForm<LoginFormSchema>({
    resolver: standardSchemaResolver(LoginFormSchema),
    defaultValues: generateDefaultValue(LoginFormSchema),
  });

  const login = useMutation({
    mutationFn: async (json: LoginFormSchema) => {
      // using raw ky to prevent the usual hooks from running
      const res = await ky.post("api/v1/auth/login", {
        prefixUrl: await getBackendUrl(),
        json,
      });
      return res.json<SigninResponse>();
    },
    onSuccess(data, vars) {
      saveTokens({ access: data.access_token, refresh: data.refresh_token });
      if (data.must_change_password) {
        setUpdateDialogPassword(vars.password);
      } else {
        navigate({ to: "/" });
      }
    },
    onError(error) {
      return setServerErrors(form, error);
    },
  });

  return (
    <>
      <PasswordUpdateFormDialog
        password={updateDialogPassword}
        onSubmit={() => {
          setUpdateDialogPassword(null);
          form.resetField("password");
          logout();
        }}
      />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-4 py-8">
        <div className="w-full space-y-6">
          <div className="space-y-2 text-center">
            <h1 className="text-4xl font-bold">Welcome back!</h1>
            <p className="text-base text-gray-500">
              Please enter your details.
            </p>
          </div>

          <Form {...form}>
            <form
              className="space-y-6"
              onSubmit={form.handleSubmit((formData) => {
                if (login.isPending) return;
                login.mutate(formData);
              })}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {" "}
                      Email <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="email"
                        placeholder="Email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {" "}
                      Password <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        autoComplete="current-password"
                        placeholder="Password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-end">
                <Link
                  to="/auth/forgot-password"
                  className="text-sm font-medium text-[#dd2590] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={login.isPending}
                className="mb-0 w-full rounded-md bg-[#dd2590] py-3 font-medium text-white transition-colors hover:bg-pink-700 disabled:opacity-60"
              >
                {login.isPending ? "Signing In..." : "Sign In"}
              </button>

              {form.formState.errors.root?.serverError ? (
                <p className="mt-2 text-sm text-destructive">
                  {form.formState.errors.root.serverError.message}
                </p>
              ) : null}
            </form>
          </Form>
        </div>
      </main>
    </>
  );
}

/**
 * Shows a failed auth request on its form: a plain `detail` as the form-level
 * error, FastAPI validation errors on their fields.
 */
async function setServerErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  error: Error,
) {
  const data: unknown =
    error instanceof HTTPError
      ? await error.response.json().catch(() => null)
      : null;
  const detail =
    data && typeof data === "object" && "detail" in data ? data.detail : null;

  if (typeof detail === "string") {
    form.setError("root.serverError", { message: detail });
    return;
  }

  const fieldErrors = parseBackendError(detail);
  if (fieldErrors === null || fieldErrors.length === 0) {
    form.setError("root.serverError", {
      message: "An unexpected error occurred",
    });
    return;
  }
  fieldErrors.forEach(([loc, message]) =>
    form.setError(loc as Path<T>, { message }),
  );
}

const resetPasswordSchema = z
  .object({
    current: z.string().min(1, "Please enter current password"),
    new: z.string().min(1, "Please enter new password"),
    confirm: z.string().min(1, "Please enter new password again"),
  })
  .refine((data) => data.new === data.confirm, {
    message: "Passwords do not match",
    path: ["confirm"],
  });

function PasswordUpdateFormDialog({
  password,
  onSubmit,
}: {
  password: string | null;
  onSubmit: () => void;
}) {
  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      ...generateDefaultValue(resetPasswordSchema),
    },
  });
  useEffect(() => {
    if (password) {
      form.reset({
        ...generateDefaultValue(resetPasswordSchema),
        current: password,
      });
      // defer setting focus because all the refs are gone between a reset and next render
      new Promise((r) => setTimeout(r, 5)).then(() => form.setFocus("new"));
    }
  }, [password]);

  const passwordChangeMutation = useMutation({
    mutationFn: api.auth.changePassword,
    onSuccess() {
      toast.success(
        "Password changed successfuly. Please sign in with your new password",
      );

      onSubmit();
    },
    onError(error) {
      return setServerErrors(form, error);
    },
  });
  return (
    <Dialog open={!!password}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Update Password</DialogTitle>
          <DialogDescription>
            The temporary password has expired, please create a permanent
            password
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((d) =>
              passwordChangeMutation.mutate(d),
            )}
            className="w-5/10 space-y-4"
          >
            <FormField
              control={form.control}
              name="current"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="new"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter new password again"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.formState.errors.root?.serverError ? (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.serverError.message}
              </p>
            ) : null}

            <Button type="submit" disabled={passwordChangeMutation.isPending}>
              Submit
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
